// Form Widgets - Input widgets for simulator configuration

import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Briefcase, UserCheck, Euro, Check, ChevronsUpDown } from "lucide-react";
import { formatCurrency } from "./helpers";
import { fetchBusinessSettings } from "@/hooks/useBusinessSettings";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ INTERMEDIARY SELECTORS (Agência / Parceiro) ============
// Configurados em Admin → Negócio. Agência e Parceiro são exclusivos entre si.
const IntermediarySelector = ({ formData, setFormData, kind }) => {
  const isPartner = kind === "partner";
  const listKey = isPartner ? "partners" : "agencies";
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchBusinessSettings().then((s) => {
      if (alive) setItems((s?.[listKey] || []).filter((a) => (a.fee_pct || 0) > 0));
    });
    return () => { alive = false; };
  }, [listKey]);

  if (items.length === 0) return null;

  const currentValue = (isPartner ? formData.selected_partner_id : formData.selected_agency_id) || "none";
  const onChange = (v) => {
    const id = v === "none" ? null : v;
    if (isPartner) {
      setFormData({ ...formData, selected_partner_id: id, ...(id ? { selected_agency_id: null, include_agency_fee: false } : {}) });
    } else {
      setFormData({ ...formData, selected_agency_id: id, include_agency_fee: !!id, ...(id ? { selected_partner_id: null } : {}) });
    }
  };
  const label = isPartner ? "Parceiro" : "Agência";
  const title = isPartner
    ? "Parceiro (configurável em Admin → Negócio). A percentagem é retirada da rate do cliente — só custo empresa. Exclusivo com Agência."
    : "Agência intermediária (configurável em Admin → Negócio). A percentagem é retirada da rate do cliente.";
  return (
    <div className="flex items-center justify-between gap-2 pt-1.5 mt-1.5 border-t border-violet-100">
      <div className="flex items-center gap-2 flex-1">
        <span className="text-xs font-medium text-orange-700 w-14" title={title}>{label}</span>
        <Select value={currentValue} onValueChange={onChange}>
          <SelectTrigger className="h-7 w-40 text-xs" data-testid={`${kind}-select`}>
            <SelectValue placeholder="Nenhuma" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" data-testid={`${kind}-option-none`}>Nenhuma</SelectItem>
            {items.map((a) => (
              <SelectItem key={a.id} value={a.id} data-testid={`${kind}-option-${a.id}`}>
                {a.name} ({a.fee_pct}%)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const AgencySelector = (props) => (
  <>
    <IntermediarySelector {...props} kind="agency" />
    <IntermediarySelector {...props} kind="partner" />
  </>
);

// ============ BUSINESS TYPE ============
export const BusinessTypeWidget = ({ formData, setFormData, allowedViews }) => {
  const av = Array.isArray(allowedViews) && allowedViews.length > 0
    ? allowedViews
    : ["outsourcing_contract", "outsourcing_green_receipts", "outsourcing_b2b", "perm"];
  const canOutsourcing = av.some((v) => v.startsWith("outsourcing_"));
  const canPerm = av.includes("perm");
  return (
  <div className="p-2 h-full">
    <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase truncate mb-1.5" title="Tipo de Negócio">Tipo de Negócio</div>
    {/* Compact pill / segmented control — saves vertical space vs the previous large icon cards */}
    <div className="inline-flex rounded-full border border-border bg-slate-50 p-0.5" data-testid="business-type-pills">
      {canOutsourcing && (
      <button
        type="button"
        onClick={() => setFormData({ ...formData, business_type: "outsourcing", include_iht: true })}
        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${formData.business_type === "outsourcing" ? "bg-[var(--brand-primary)] text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}
        data-testid="business-type-outsourcing"
      >
        <Briefcase className="w-3.5 h-3.5" />
        Outsourcing
      </button>
      )}
      {canPerm && (
      <button
        type="button"
        onClick={() => setFormData({ ...formData, business_type: "perm", include_iht: false })}
        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${formData.business_type === "perm" ? "bg-[var(--brand-primary)] text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}
        data-testid="business-type-perm"
      >
        <UserCheck className="w-3.5 h-3.5" />
        Perm
      </button>
      )}
    </div>
  </div>
  );
};

// ============ CONSULTANT ============
export const ConsultantWidget = ({ consultantName, setConsultantName, consultantBirthDate, setConsultantBirthDate, consultantNif, setConsultantNif, consultantEmail, setConsultantEmail, consultantFunction, setConsultantFunction, jobFunctions, formData, setFormData }) => {
  const [fnOpen, setFnOpen] = useState(false);
  
  return (
  <div className="p-2 h-full">
    <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Dados Candidato">Dados Candidato</div>
    <div className="grid gap-2" style={{ gridTemplateColumns: '2fr 1fr 1.5fr' }}>
      <div className="min-w-0">
        <label className="text-xs font-medium text-slate-500 truncate block" title="Nome">Nome</label>
        <Input 
          value={consultantName} 
          onChange={(e) => setConsultantName(e.target.value)}
          placeholder="Nome"
          className="h-8 text-xs sm:text-sm text-slate-800"
          data-testid="consultant-name"
        />
      </div>
      <div className="min-w-0">
        <label className="text-xs font-medium text-slate-500 truncate block" title="NIF">NIF</label>
        <Input 
          value={consultantNif} 
          onChange={(e) => setConsultantNif(e.target.value)}
          placeholder="NIF"
          className="h-8 text-xs sm:text-sm text-slate-800"
          maxLength={9}
          data-testid="consultant-nif"
        />
      </div>
      <div className="min-w-0">
        <label className="text-xs font-medium text-slate-500 truncate block" title="Email">Email</label>
        <Input 
          type="email"
          value={consultantEmail} 
          onChange={(e) => setConsultantEmail(e.target.value)}
          placeholder="email@..."
          className="h-8 text-xs sm:text-sm text-slate-800"
          data-testid="consultant-email"
        />
      </div>
    </div>
    <div className="grid gap-2 mt-2" style={{ gridTemplateColumns: '1fr 2fr' }}>
      <div className="min-w-0">
        <label className="text-xs font-medium text-slate-500 truncate block" title="Data de Nascimento">Nascimento</label>
        <Input 
          type="date" 
          value={consultantBirthDate} 
          onChange={(e) => setConsultantBirthDate(e.target.value)}
          className="date-compact h-8 text-[11px] text-slate-800 px-1"
        />
      </div>
      <div className="min-w-0">
      <label className="text-xs font-medium text-slate-500 block" title="Função">Função</label>
      <Popover open={fnOpen} onOpenChange={setFnOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-xs sm:text-sm text-slate-800 hover:bg-accent hover:text-accent-foreground"
            data-testid="consultant-function"
          >
            <span className={consultantFunction ? "" : "text-muted-foreground"}>
              {consultantFunction || "Pesquisar função..."}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Pesquisar função..." className="h-9" />
            <CommandList>
              <CommandEmpty>Nenhuma função encontrada.</CommandEmpty>
              <CommandGroup>
                {(jobFunctions || []).map(f => (
                  <CommandItem
                    key={f.id}
                    value={f.name}
                    onSelect={() => {
                      setConsultantFunction(f.name);
                      setFnOpen(false);
                    }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${consultantFunction === f.name ? "opacity-100" : "opacity-0"}`} />
                    {f.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      </div>
    </div>
    {formData && formData.business_type !== "perm" && (
      <div className="flex items-center gap-1 mt-2">
        <button
          type="button"
          onClick={() => setFormData({ ...formData, placement_type: "national" })}
          className={`flex-1 px-2 py-1 rounded text-[10px] font-medium border transition-all ${formData.placement_type !== "international" ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-slate-500 border-slate-200"}`}
          data-testid="placement-national"
        >
          Nacional
        </button>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, placement_type: "international" })}
          className={`flex-1 px-2 py-1 rounded text-[10px] font-medium border transition-all ${formData.placement_type === "international" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-200"}`}
          data-testid="placement-international"
        >
          Internacional
        </button>
      </div>
    )}
  </div>
  );
};

// ============ REGIME ============
export const RegimeWidget = ({ formData, setFormData, allowedViews }) => {
  const av = Array.isArray(allowedViews) && allowedViews.length > 0
    ? allowedViews
    : ["outsourcing_contract", "outsourcing_green_receipts", "outsourcing_b2b", "perm"];
  const canContract = av.includes("outsourcing_contract");
  const canGR = av.includes("outsourcing_green_receipts");
  const canB2B = av.includes("outsourcing_b2b");

  if (formData.business_type === "perm") {
    return (
      <div className="p-2 h-full">
        <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Tipo de Regime">Tipo de Regime</div>
        <div className="flex items-center justify-center h-12">
          <span className="px-4 py-2 rounded-lg border-2 border-primary bg-primary text-primary-foreground text-xs sm:text-sm font-medium">
            Contrato
          </span>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-1">Obrigatório para Perm</p>
      </div>
    );
  }
  
  return (
    <div className="p-2 h-full">
      <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-1.5 truncate" title="Tipo de Regime">Tipo de Regime</div>
      {/* Compact pill / segmented control */}
      <div className="inline-flex rounded-full border border-border bg-slate-50 p-0.5" data-testid="regime-pills">
        {canContract && (
        <button
          type="button"
          onClick={() => setFormData({ ...formData, regime_type: "contract" })}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${formData.regime_type === "contract" ? "bg-[var(--brand-primary)] text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}
          data-testid="regime-contract"
        >
          Contrato
        </button>
        )}
        {canGR && (
        <button
          type="button"
          onClick={() => setFormData({ ...formData, regime_type: "green_receipts" })}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${formData.regime_type === "green_receipts" ? "bg-[var(--brand-primary)] text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}
          data-testid="regime-green-receipts"
        >
          Recibos
        </button>
        )}
        {canB2B && (
        <button
          type="button"
          onClick={() => setFormData({ ...formData, regime_type: "b2b" })}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${formData.regime_type === "b2b" ? "bg-[var(--brand-primary)] text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}
          data-testid="regime-b2b"
        >
          B2B
        </button>
        )}
      </div>
      {formData.regime_type === "green_receipts" && (
        <div
          className="mt-2 px-2 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-[10px] sm:text-xs text-emerald-800 leading-tight"
          data-testid="rv-legal-banner"
          title="Código dos Regimes Contributivos, art. 153.º-A"
        >
          <span className="font-semibold">1º ano de atividade?</span> Recibos Verdes está <strong>isento de SS por lei</strong>. Ativa o seletor abaixo &quot;1º Ano de Atividade&quot;.
        </div>
      )}
    </div>
  );
};

// ============ PROPOSAL ============
export const ProposalWidget = ({ formData, setFormData, isAdmin = false, result = null }) => {
  const isPerm = formData.business_type === "perm";
  const isRecibosOrB2B = formData.regime_type === "green_receipts" || formData.regime_type === "b2b";
  
  if (isPerm) {
    return (
      <div className="p-2 h-full">
        <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Candidato">Candidato</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative min-w-0">
            <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input 
              type="number"
              placeholder="Salário" 
              value={formData.perm_candidate_salary || ""} 
              onChange={(e) => setFormData({ ...formData, perm_candidate_salary: e.target.value })}
              className="h-8 text-xs sm:text-sm pl-7 text-slate-800"
              title="Salário Bruto do Candidato"
            />
          </div>
          <Select 
            value={formData.perm_salary_type === "monthly" ? "monthly" : `annual_${formData.perm_salary_months || 14}`} 
            onValueChange={(v) => {
              if (v === "monthly") setFormData({ ...formData, perm_salary_type: "monthly" });
              else {
                const months = parseInt(v.split("_")[1] || "14");
                setFormData({ ...formData, perm_salary_type: "annual", perm_salary_months: months });
              }
            }}
          >
            <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0" data-testid="perm-salary-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Mensal</SelectItem>
              <SelectItem value="annual_12">Anual × 12</SelectItem>
              <SelectItem value="annual_14">Anual × 14</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }
  
  if (isRecibosOrB2B) {
    // PROPOSTA e NEGÓCIO são conceitos independentes.
    // - PROPOSTA (daily_rate): rate do freelancer (o que a empresa paga ao consultor)
    // - NEGÓCIO (client_rate): rate do cliente (o que a empresa cobra ao cliente final)
    // Podem divergir em cenários com margem de agência. NENHUM auto-sync entre eles.
    return (
      <div className="p-2 h-full">
        <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Proposta (Recibos/B2B)">Proposta</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative min-w-0">
            <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input 
              type="number"
              placeholder={formData.value_type === "monthly" ? "Bruto Mensal" : "Rate"} 
              value={formData.daily_rate || ""} 
              onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
              className="h-8 text-xs sm:text-sm pl-7 text-slate-800"
              title={formData.value_type === "monthly" ? "Valor Bruto Mensal" : "Rate"}
              data-testid="proposal-rate-input"
            />
          </div>
          <Select value={formData.value_type} onValueChange={(v) => setFormData({ ...formData, value_type: v })}>
            <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0" data-testid="proposal-value-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Mês</SelectItem>
              <SelectItem value="daily">Dia</SelectItem>
              <SelectItem value="hourly">Hora</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Meses de PAGAMENTO ao freelancer (11 ou 12).
            IMPORTANTE: isto é INDEPENDENTE dos "MESES" no widget NEGÓCIO,
            que controlam quantos meses o cliente factura à empresa (podem
            ser 10, 10.5, 11, 10.75, etc.). Aqui só se decide se o freelancer
            recebe 11 ou 12 meses.  → mapeia para `payment_months` no backend. */}
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] uppercase text-muted-foreground font-semibold">Meses</span>
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="freelancer-billing-months">
            {[11, 12].map(m => (
              <button
                type="button"
                key={m}
                onClick={() => setFormData({
                  ...formData,
                  freelancer_billing_months: m,
                  payment_months: m,   // ← campo do backend que controla nº meses de pagamento ao freelancer
                })}
                className={`px-2.5 py-0.5 text-[11px] font-medium transition-colors ${parseInt(formData.freelancer_billing_months || formData.payment_months || 11) === m ? "bg-brand-600 text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
                data-testid={`freelancer-billing-months-${m}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        {/* 1º Ano de Atividade — apenas para Recibos Verdes (B2B não tem este regime) */}
        {formData.regime_type === "green_receipts" && (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={(formData?.ss_activity_year || 0) === 1}
                onChange={(e) => setFormData({
                  ...formData,
                  ss_activity_year: e.target.checked ? 1 : 0,
                  first_year_ss_exempt: e.target.checked,
                })}
                className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                data-testid="ss-first-year-checkbox"
              />
              <span className="text-xs font-medium text-slate-700">1º Ano de Atividade</span>
            </label>
            <p className="text-[10px] text-muted-foreground mt-1 ml-6">
              {(formData?.ss_activity_year || 0) === 1
                ? "Isenção total SS (0% — art. 153.º-A Cód. Reg. Contributivos)"
                : "Taxa SS completa"}
            </p>
            <div className="flex items-center justify-between gap-2 mt-2" title="Contribuição da empresa como Entidade Contratante (art. 168.º CRC). Só custo empresa; não se aplica no 1.º ano isento nem abaixo de 6 × IAS/ano.">
              <span className="text-xs font-medium text-slate-700">Dependência económica</span>
              <Select value={formData.rv_dependency_level || "over_80"} onValueChange={(v) => setFormData({ ...formData, rv_dependency_level: v })}>
                <SelectTrigger className="h-7 w-44 text-xs" data-testid="rv-dependency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="rv-dependency-none">Nenhuma (até 50%)</SelectItem>
                  <SelectItem value="50_80" data-testid="rv-dependency-50-80">50–80% (7%)</SelectItem>
                  <SelectItem value="over_80" data-testid="rv-dependency-over-80">Mais de 80% (10%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {(formData?.ss_activity_year || 0) === 1
                ? "SS Entidade Contratante: isenta no 1.º ano"
                : "SS Entidade Contratante — custo empresa sobre o valor pago"}
            </p>
          </div>
        )}
        {/* Welcome Kit — visível apenas para admin. Custo empresa, não afecta líquido. */}
        {isAdmin && (
          <div className="flex items-center justify-between gap-2 pt-1.5 mt-1.5 border-t border-violet-100" data-testid="welcome-kit-block-freelancer">
            <div className="flex items-center gap-2">
              <Switch
                checked={!!formData.include_welcome_kit}
                onCheckedChange={(v) => setFormData({ ...formData, include_welcome_kit: v })}
                className="scale-75"
                data-testid="include-welcome-kit-toggle-freelancer"
              />
              <span className="text-xs font-medium text-violet-700" title="Custo único da empresa por novo colaborador. Diluído nos primeiros N meses. Não aparece ao colaborador.">
                Welcome Kit
              </span>
            </div>
            {formData.include_welcome_kit && (
              <span className="text-[10px] text-violet-600 font-medium">custo empresa</span>
            )}
          </div>
        )}
        {/* Agência — selector de intermediário (custo empresa) */}
        <AgencySelector formData={formData} setFormData={setFormData} />
      </div>
    );
  }
  
  return (
    <div className="p-2 h-full">
      <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Proposta Salarial">Proposta</div>
      <div className="relative min-w-0">
        <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
        <Input 
          type="number"
          inputMode="decimal"
          step="0.01"
          placeholder="Salário Bruto" 
          value={formData.gross_value} 
          onChange={(e) => setFormData({ ...formData, gross_value: (e.target.value || "").replace(",", "."), value_type: "monthly" })}
          className="h-8 text-xs sm:text-sm pl-7 text-slate-800"
          title="Salário Bruto Mensal (aceita decimais com . ou ,)"
          data-testid="proposal-gross-input"
        />
      </div>
      <div className="flex items-center justify-between mt-2 p-1.5 rounded-lg bg-white border-2 border-brand-600" data-testid="iht-toggle">
        <div className="flex items-center gap-1.5">
          {formData.business_type === "outsourcing" ? (
            <span className="text-xs font-medium text-brand-600">IHT (80/20) Automático</span>
          ) : (
            <>
              <Switch
                checked={formData.include_iht}
                onCheckedChange={(v) => setFormData({ ...formData, include_iht: v })}
                className="scale-75"
              />
              <span className="text-xs font-medium text-brand-600">IHT (80/20)</span>
            </>
          )}
        </div>
        {formData.include_iht && formData.gross_value > 0 && (
          <span className="text-[10px] text-brand-600 font-medium">{Math.round(formData.gross_value * 0.80)}€ + {Math.round(formData.gross_value * 0.20)}€</span>
        )}
      </div>
      {/* Welcome Kit — visível apenas para admin. Para outros papéis (Recruiter/Business)
          o Welcome Kit está sempre activo por defeito e continua a impactar o custo/margem
          mas o switch fica escondido para simplificar o simulador. */}
      {isAdmin && (
      <div className="flex items-center justify-between gap-2 pt-1.5 mt-1.5 border-t border-violet-100" data-testid="welcome-kit-block">
        <div className="flex items-center gap-2">
          <Switch
            checked={!!formData.include_welcome_kit}
            onCheckedChange={(v) => setFormData({ ...formData, include_welcome_kit: v })}
            className="scale-75"
            data-testid="include-welcome-kit-toggle"
          />
          <span className="text-xs font-medium text-violet-700" title="Custo único da empresa por novo colaborador. Diluído nos primeiros N meses (configurável em Admin → Configurações de Negócio). Não aparece ao colaborador.">
            Welcome Kit
          </span>
        </div>
        {formData.include_welcome_kit && (
          <span className="text-[10px] text-violet-600 font-medium">custo empresa</span>
        )}
      </div>
      )}
      {/* Agência — selector de intermediário (custo empresa, retirado da rate cliente) */}
      <AgencySelector formData={formData} setFormData={setFormData} />
      {/* Aviso % Benefícios — soma dos benefícios que NÃO são pagamentos
          regulares (KM + Real Vida + Ticket Infância) sobre o Salário Bruto.
          Sub. Refeição e Seguro de Saúde EXCLUÍDOS por decisão de negócio:
          esses dois entram como pagamento normal / benefício estruturado e
          não contam para o "risco fiscal" desta indicação. Mesma fórmula
          usada em ResultWidgets e SharedSimulator para consistência. */}
      {(() => {
        const gross = parseFloat(result?.gross_monthly) || parseFloat(formData.gross_value) || 0;
        if (gross <= 0) return null;
        const km = Number(result?.km_allowance ?? formData.km_allowance) || 0;
        const realVida = Number(result?.insurance_capitalization ?? formData.insurance_capitalization) || 0;
        const ticket = Number(result?.ticket_infancia ?? formData.ticket_infancia) || 0;
        const benefits = km + realVida + ticket;
        const pct = (benefits / gross) * 100;
        // Semáforo: verde ≤25%, amarelo 25-35%, vermelho >35%
        const tone =
          pct > 35
            ? { bg: "bg-red-50", border: "border-red-300", text: "text-red-700" }
            : pct > 25
            ? { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700" }
            : { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700" };
        return (
          <div
            className={`mt-1.5 p-1.5 rounded-lg border-2 flex items-center justify-between ${tone.bg} ${tone.border}`}
            title="(KM + Real Vida + Ticket Infância) ÷ Salário Bruto · Sub. Refeição e Seguro Saúde excluídos por decisão de negócio · Verde ≤25%, Amarelo 25–35%, Vermelho >35%"
            data-testid="benefits-pct-warning"
          >
            <span className={`text-xs font-medium ${tone.text}`}>
              % Benefícios
            </span>
            <span className={`text-[11px] font-semibold font-mono ${tone.text}`}>
              {pct.toFixed(1)}%
            </span>
          </div>
        );
      })()}

      {/* ===== SITUAÇÃO FISCAL (consolidada com a Proposta) ===== */}
      <div className="mt-2 pt-2 border-t border-slate-200" data-testid="proposal-fiscal-block">
        <div className="text-[10px] font-semibold text-brand-600 uppercase mb-1 tracking-wide">Situação Fiscal</div>
        <div className="grid grid-cols-2 gap-1 sm:gap-2">
          <Select value={formData.irs_type} onValueChange={(v) => setFormData({ ...formData, irs_type: v })}>
            <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Solteiro</SelectItem>
              <SelectItem value="married_1">Casado 1T</SelectItem>
              <SelectItem value="married_2">Casado 2T</SelectItem>
              <SelectItem value="divorced">Divorciado(a)</SelectItem>
              <SelectItem value="widowed">Viúvo(a)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={(formData.dependents ?? 0).toString()} onValueChange={(v) => setFormData({ ...formData, dependents: parseInt(v) })}>
            <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0 dep.</SelectItem>
              <SelectItem value="1">1 dep.</SelectItem>
              <SelectItem value="2">2 dep.</SelectItem>
              <SelectItem value="3">3 dep.</SelectItem>
              <SelectItem value="4">4 dep.</SelectItem>
              <SelectItem value="5">5+ dep.</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-1.5 flex items-center justify-between p-1.5 rounded-lg bg-white border-2 border-brand-600" data-testid="irs-jovem-toggle">
          <div className="flex items-center gap-1.5 min-w-0">
            <Switch
              checked={formData.irs_jovem}
              onCheckedChange={(v) => setFormData({ ...formData, irs_jovem: v })}
              className="scale-75 flex-shrink-0"
            />
            <span className="text-xs font-medium text-brand-600 truncate">IRS Jovem</span>
          </div>
          {formData.irs_jovem && (
            <Select value={(formData.irs_jovem_year || 1).toString()} onValueChange={(v) => setFormData({ ...formData, irs_jovem_year: parseInt(v) })}>
              <SelectTrigger className="h-6 w-16 text-[10px] text-brand-600 border-brand-600 min-w-0 flex-shrink-0" data-testid="irs-jovem-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5,6,7,8,9,10].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}.º ano</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 p-1.5 rounded-lg bg-white border-2 border-brand-600">
          <Switch
            checked={formData.has_disability || false}
            onCheckedChange={(v) => setFormData({ ...formData, has_disability: v })}
            className="scale-75 flex-shrink-0"
            data-testid="has-disability-switch"
          />
          <span className="text-xs font-medium text-brand-600" title="Pessoa com deficiência ≥60% — usa Tabelas IV-VII">Deficiência ≥60%</span>
        </div>
      </div>
    </div>
  );
};

// ============ PROJECT / NEGÓCIO ============
export const ProjectWidget = ({ formData, setFormData, equipment, licenses, clientSpecialFees, clients = [], results }) => {
  const { isAdmin } = useAuth();
  const isPerm = formData.business_type === "perm";

  if (isPerm) {
    // Escalão aplicado automaticamente (lido do backoffice). Quando o backend devolve `auto_tier`,
    // a % do input é informativa e read-only — reflete o escalão correspondente ao fee_base actual.
    const autoTier = results?.results?.[0]?.breakdown?.auto_tier;
    const effectivePct = autoTier?.fee_percentage ?? formData.perm_percentage_fee;
    return (
      <div className="p-2 h-full">
        <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Fee de Recrutamento (automático por escalão)">Fee Recrutamento</div>
        <div className="grid grid-cols-3 gap-1 sm:gap-2">
          <Select value={formData.perm_fee_type} onValueChange={(v) => setFormData({ ...formData, perm_fee_type: v })}>
            <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">%</SelectItem>
              <SelectItem value="fixed">Fixo</SelectItem>
              <SelectItem value="both">Ambos</SelectItem>
            </SelectContent>
          </Select>
          {(formData.perm_fee_type === "percentage" || formData.perm_fee_type === "both") && (
            <Input
              type="number"
              placeholder="%"
              value={effectivePct}
              readOnly
              tabIndex={-1}
              className="h-8 text-xs sm:text-sm text-slate-800 bg-slate-100 cursor-not-allowed"
              title={autoTier ? `Escalão auto: ${autoTier.description || `${autoTier.min_salary}€+`}` : "% do escalão (auto)"}
              data-testid="perm-fee-pct-auto"
            />
          )}
          {(formData.perm_fee_type === "fixed" || formData.perm_fee_type === "both") && (
            <div className="relative min-w-0">
              <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                type="number"
                placeholder="€"
                value={formData.perm_fixed_fee}
                onChange={(e) => setFormData({ ...formData, perm_fixed_fee: e.target.value })}
                className="h-8 text-xs sm:text-sm pl-7 text-slate-800"
                title="Fee Fixo"
              />
            </div>
          )}
        </div>
        {autoTier && (
          <div className="mt-1.5 text-[10px] text-slate-500 truncate" title={autoTier.description}>
            Escalão: <span className="font-semibold text-brand-700">{autoTier.description || `≥ ${autoTier.min_salary}€`}</span> → {autoTier.fee_percentage}%
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="p-2 h-full">
      <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Negócio">Negócio</div>
      {/* Cliente — quando escolhido pré-preenche prazo + meses faturáveis */}
      {clients && clients.length > 0 && (
        <div className="mb-2 flex items-center gap-2">
          <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide flex-shrink-0">Cliente</span>
          <Select
            value={formData.client_id || "none"}
            onValueChange={(v) => {
              if (v === "none") {
                setFormData({ ...formData, client_id: "" });
                return;
              }
              const c = clients.find((x) => x.id === v);
              if (!c) {
                setFormData({ ...formData, client_id: v });
                return;
              }
              setFormData({
                ...formData,
                client_id: c.id,
                payment_terms: c.payment_terms ?? formData.payment_terms,
                billable_months: c.billable_months ?? formData.billable_months,
              });
            }}
          >
            <SelectTrigger
              className="h-8 text-xs sm:text-sm text-slate-800 w-full min-w-0"
              title="Selecionar cliente para aplicar prazo e meses faturáveis"
              data-testid="client-select"
            >
              <SelectValue placeholder="— sem cliente —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— sem cliente —</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} <span className="text-slate-400 ml-1">({c.payment_terms}d · {c.billable_months}m)</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-4 gap-2">
        <div className="space-y-1 min-w-0">
          <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Rate</span>
          <div className="relative">
            <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input 
              type="number"
              inputMode="decimal"
              step="0.01"
              placeholder="Rate" 
              value={formData.client_rate} 
              onChange={(e) => setFormData({ ...formData, client_rate: (e.target.value || "").replace(",", ".") })}
              className="h-8 text-xs sm:text-sm pl-7 text-slate-800 disabled:bg-slate-100 disabled:text-slate-400 w-full"
              title={formData.is_fix_fee ? "Bloqueado — está a usar Fix Fee" : "Rate Cliente (aceita decimais com . ou ,)"}
              disabled={!!formData.is_fix_fee}
              data-testid="client-rate-input"
            />
          </div>
        </div>
        <div className="space-y-1 min-w-0">
          <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Tipo</span>
          <Select
            value={formData.client_rate_type || "hourly"}
            onValueChange={(v) => setFormData({ ...formData, client_rate_type: v })}
            disabled={!!formData.is_fix_fee}
          >
            <SelectTrigger
              className="h-8 text-xs sm:text-sm text-slate-800 w-full disabled:bg-slate-100 disabled:text-slate-400"
              title={formData.is_fix_fee ? "Bloqueado — está a usar Fix Fee" : "Tipo de Rate"}
              data-testid="client-rate-type-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hourly">Hora</SelectItem>
              <SelectItem value="daily">Dia</SelectItem>
              <SelectItem value="monthly">Mês</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 min-w-0">
          <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Meses</span>
          <Input 
            type="number"
            inputMode="decimal"
            step="0.25"
            min="0"
            max="12"
            placeholder="11" 
            value={formData.billable_months} 
            onChange={(e) => setFormData({ ...formData, billable_months: (e.target.value || "").replace(",", ".") })}
            className="h-8 text-xs sm:text-sm text-slate-800 w-full"
            title="Meses Faturáveis (aceita decimais com . ou ,)"
          />
        </div>
        <div className="space-y-1 min-w-0">
          <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Prazo</span>
          <Select value={formData.payment_terms?.toString() || "30"} onValueChange={(v) => setFormData({ ...formData, payment_terms: parseInt(v) })}>
            <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30d</SelectItem>
              <SelectItem value="45">45d</SelectItem>
              <SelectItem value="60">60d</SelectItem>
              <SelectItem value="90">90d</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Fix Fee — pisco que assume valor fixo no Negócio */}
      <div className="mt-2 flex items-center gap-2">
        <input type="checkbox" checked={!!formData.is_fix_fee} onChange={(e) => setFormData({ ...formData, is_fix_fee: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500 flex-shrink-0" data-testid="fix-fee-checkbox" />
        <span className="text-xs font-medium text-slate-500 flex-shrink-0">Fix Fee</span>
        <Input
          type="number"
          value={formData.fix_fee_value || ""}
          onChange={(e) => setFormData({ ...formData, fix_fee_value: parseFloat(e.target.value) || 0 })}
          className="h-7 w-28 text-xs text-right text-slate-800 font-mono"
          placeholder="Valor fixo"
          disabled={!formData.is_fix_fee}
          data-testid="fix-fee-value"
        />
        <Select
          value={formData.fix_fee_type || "monthly"}
          onValueChange={(v) => setFormData({ ...formData, fix_fee_type: v })}
          disabled={!formData.is_fix_fee}
        >
          <SelectTrigger
            className="h-7 w-24 text-xs text-slate-800 disabled:bg-slate-100 disabled:text-slate-400"
            data-testid="fix-fee-type-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hourly">Hora</SelectItem>
            <SelectItem value="daily">Dia</SelectItem>
            <SelectItem value="monthly">Mês</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] text-slate-400 flex-shrink-0">
          {formData.is_fix_fee ? `€/${(formData.fix_fee_type || "monthly") === "hourly" ? "hora" : (formData.fix_fee_type === "daily" ? "dia" : "mês")} fixo` : "€/mês fixo"}
        </span>
        {formData.is_fix_fee && (
          <label className="flex items-center gap-1 text-[10px] text-pink-700 flex-shrink-0 cursor-pointer" title="Fix Fee faturado 12 meses (ignora os Meses faturáveis)">
            <input type="checkbox" checked={formData.fix_fee_12_months !== false} onChange={(e) => setFormData({ ...formData, fix_fee_12_months: e.target.checked })} className="w-3.5 h-3.5 rounded border-slate-300 text-pink-600" data-testid="fix-fee-12-months-checkbox" />
            Faturar 12 meses
          </label>
        )}
      </div>
      {isAdmin && (
        <div className="mt-2 flex items-center gap-2" data-testid="hours-per-day-row">
          <span className="text-xs font-medium text-slate-500 flex-shrink-0">Horas/dia</span>
          <Input
            type="number" inputMode="decimal" step="0.5" min="1" max="12"
            value={formData.hours_per_day ?? 8}
            onChange={(e) => setFormData({ ...formData, hours_per_day: (e.target.value || "").replace(",", ".") })}
            className="h-7 w-20 text-xs text-right text-slate-800 font-mono"
            data-testid="hours-per-day-input"
          />
          <span className="text-[10px] text-slate-400">Admin · horas/mês = horas/dia × dias úteis médios do Calendário</span>
        </div>
      )}
      {/* Equipamento integrado no Negócio — múltipla escolha */}
      {equipment && equipment.length > 0 && (() => {
        const selectedIds = Array.isArray(formData.equipment_ids) ? formData.equipment_ids : (formData.equipment_id ? [formData.equipment_id] : []);
        const toggleEq = (id) => {
          const next = selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id];
          setFormData({ ...formData, equipment_ids: next, equipment_id: next[0] || "" });
        };
        const totalEq = equipment
          .filter(e => selectedIds.includes(e.id || e._id))
          .reduce((s, e) => s + (e.monthly_cost || 0), 0);
        return (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <div className="flex items-start gap-2">
              <span className="text-xs font-medium text-slate-500 flex-shrink-0 mt-1">Equipamento</span>
              <div className="flex-1 min-w-0">
                <details className="group" data-testid="equipment-multi">
                  <summary className="cursor-pointer text-xs px-2 py-1 border border-slate-200 rounded bg-white text-slate-700 hover:bg-slate-50">
                    {selectedIds.length === 0
                      ? "Selecionar equipamentos…"
                      : `${selectedIds.length} selecionado(s)`}
                  </summary>
                  <div className="mt-1 p-2 border border-slate-200 rounded bg-white max-h-44 overflow-y-auto space-y-1">
                    {equipment.map((eq) => {
                      const eid = eq.id || eq._id;
                      const checked = selectedIds.includes(eid);
                      return (
                        <label key={eid} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                          <input type="checkbox" checked={checked} onChange={() => toggleEq(eid)} className="w-3.5 h-3.5" data-testid={`equipment-cb-${eid}`} />
                          <span className="flex-1 truncate">{eq.name}</span>
                          {/* Preço €/mês oculto no frontoffice a pedido do PO — mantido nos totais do resumo. */}
                        </label>
                      );
                    })}
                  </div>
                </details>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Licenças integradas no Negócio — múltipla escolha */}
      {licenses && licenses.length > 0 && (() => {
        const selectedIds = Array.isArray(formData.license_ids) ? formData.license_ids : (formData.license_id ? [formData.license_id] : []);
        const toggleLic = (id) => {
          const next = selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id];
          setFormData({ ...formData, license_ids: next, license_id: next[0] || "" });
        };
        const totalLic = licenses
          .filter(l => selectedIds.includes(l.id || l._id))
          .reduce((s, l) => s + (l.monthly_cost || 0), 0);
        return (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <div className="flex items-start gap-2">
              <span className="text-xs font-medium text-slate-500 flex-shrink-0 mt-1">Licenças</span>
              <div className="flex-1 min-w-0">
                <details className="group" data-testid="licenses-multi">
                  <summary className="cursor-pointer text-xs px-2 py-1 border border-slate-200 rounded bg-white text-slate-700 hover:bg-slate-50">
                    {selectedIds.length === 0
                      ? "Selecionar licenças…"
                      : `${selectedIds.length} selecionada(s)`}
                  </summary>
                  <div className="mt-1 p-2 border border-slate-200 rounded bg-white max-h-44 overflow-y-auto space-y-1">
                    {licenses.map((lic) => {
                      const lid = lic.id || lic._id;
                      const checked = selectedIds.includes(lid);
                      return (
                        <label key={lid} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                          <input type="checkbox" checked={checked} onChange={() => toggleLic(lid)} className="w-3.5 h-3.5" data-testid={`license-cb-${lid}`} />
                          <span className="flex-1 truncate">{lic.name}</span>
                          {/* Preço €/mês oculto no frontoffice a pedido do PO — mantido nos totais do resumo. */}
                        </label>
                      );
                    })}
                  </div>
                </details>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Situações Especiais — Sub-fee por cliente (apenas Perm) */}
      {formData?.business_type === "perm" && clientSpecialFees && clientSpecialFees.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={!!formData.client_special_fee_id} onChange={(e) => {
              if (!e.target.checked) setFormData({ ...formData, client_special_fee_id: "" });
              else setFormData({ ...formData, client_special_fee_id: clientSpecialFees[0]?.id });
            }} className="w-4 h-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500 flex-shrink-0" data-testid="client-special-checkbox" />
            <span className="text-xs font-medium text-slate-500 flex-shrink-0">Situação Especial</span>
            <Select value={formData.client_special_fee_id || ""} onValueChange={(v) => setFormData({ ...formData, client_special_fee_id: v })} disabled={!formData.client_special_fee_id}>
              <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0 flex-1" data-testid="client-special-select">
                <SelectValue placeholder="Selecionar cliente" />
              </SelectTrigger>
              <SelectContent>
                {clientSpecialFees.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.client_name}{c.fee_percentage > 0 ? ` — ${c.fee_percentage}%` : ""}{c.fee_fixed > 0 ? ` +${formatCurrency(c.fee_fixed)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ FISCAL ============
export const FiscalWidget = ({ formData, setFormData }) => (
  <div className="p-2 h-full">
    <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Situação Fiscal">Situação Fiscal</div>
    <div className="grid grid-cols-2 gap-1 sm:gap-2">
      <Select value={formData.irs_type} onValueChange={(v) => setFormData({ ...formData, irs_type: v })}>
        <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="single">Solteiro</SelectItem>
          <SelectItem value="married_1">Casado 1T</SelectItem>
          <SelectItem value="married_2">Casado 2T</SelectItem>
          <SelectItem value="divorced">Divorciado(a)</SelectItem>
          <SelectItem value="widowed">Viúvo(a)</SelectItem>
        </SelectContent>
      </Select>
      <Select value={formData.dependents.toString()} onValueChange={(v) => setFormData({ ...formData, dependents: parseInt(v) })}>
        <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">0 dep.</SelectItem>
          <SelectItem value="1">1 dep.</SelectItem>
          <SelectItem value="2">2 dep.</SelectItem>
          <SelectItem value="3">3 dep.</SelectItem>
          <SelectItem value="4">4 dep.</SelectItem>
          <SelectItem value="5">5+ dep.</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="mt-2 flex items-center justify-between p-1.5 rounded-lg bg-white border-2 border-brand-600" data-testid="irs-jovem-toggle">
      <div className="flex items-center gap-1.5 min-w-0">
        <Switch
          checked={formData.irs_jovem}
          onCheckedChange={(v) => setFormData({ ...formData, irs_jovem: v })}
          className="scale-75 flex-shrink-0"
        />
        <span className="text-xs font-medium text-brand-600 truncate">IRS Jovem</span>
      </div>
      {formData.irs_jovem && (
        <Select value={(formData.irs_jovem_year || 1).toString()} onValueChange={(v) => setFormData({ ...formData, irs_jovem_year: parseInt(v) })}>
          <SelectTrigger className="h-6 w-16 text-[10px] text-brand-600 border-brand-600 min-w-0 flex-shrink-0" data-testid="irs-jovem-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1.º ano</SelectItem>
            <SelectItem value="2">2.º ano</SelectItem>
            <SelectItem value="3">3.º ano</SelectItem>
            <SelectItem value="4">4.º ano</SelectItem>
            <SelectItem value="5">5.º ano</SelectItem>
            <SelectItem value="6">6.º ano</SelectItem>
            <SelectItem value="7">7.º ano</SelectItem>
            <SelectItem value="8">8.º ano</SelectItem>
            <SelectItem value="9">9.º ano</SelectItem>
            <SelectItem value="10">10.º ano</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
    <div className="mt-1.5 flex items-center gap-1.5 p-1.5 rounded-lg bg-white border-2 border-brand-600">
      <Switch
        checked={formData.has_disability || false}
        onCheckedChange={(v) => setFormData({ ...formData, has_disability: v })}
        className="scale-75 flex-shrink-0"
        data-testid="has-disability-switch"
      />
      <span className="text-xs font-medium text-brand-600" title="Pessoa com deficiência ≥60% — usa Tabelas IV-VII">Deficiência ≥60%</span>
    </div>
  </div>
);

// ============ EQUIPMENT (standalone - legacy) ============
export const EquipmentWidget = ({ formData, setFormData, equipment }) => (
  <div className="p-2 h-full">
    <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase mb-2 truncate" title="Equipamento">Equipamento</div>
    <Select value={formData.equipment_id || "none"} onValueChange={(v) => setFormData({ ...formData, equipment_id: v === "none" ? "" : v })}>
      <SelectTrigger className="h-8 text-xs sm:text-sm text-slate-800 min-w-0">
        <SelectValue placeholder="Selecionar" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Nenhum</SelectItem>
        {equipment.map((eq) => (
          <SelectItem key={eq.id || eq._id} value={eq.id || eq._id}>
            {eq.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// ============ MANUAL PREMIUM (ADMIN-ONLY) ============
// One-shot bonus value that enters company cost as a single month equivalent.
// Visible only to admin users. Applies to all regimes (Contract, B2B, Recibos
// Verdes and PERM). For Contract/B2B/RV, the value is diluted as `value/12` in
// the monthly cost breakdown. For PERM (one-shot fee) the value is added
// directly to the perm total cost.
export const ManualPremiumWidget = ({ formData, setFormData }) => (
  <div className="p-2 h-full space-y-2" data-testid="manual-premium-widget">
    <div className="text-xs sm:text-sm font-semibold text-brand-600 uppercase truncate" title="Prémios (admin)">
      Prémios <span className="ml-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0.5 rounded">admin</span>
    </div>
    <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-rose-50 border border-rose-200">
      <div className="flex items-center gap-2">
        <Switch
          checked={!!formData.include_manual_premium}
          onCheckedChange={(v) => setFormData({ ...formData, include_manual_premium: v })}
          className="scale-75"
          data-testid="include-manual-premium-toggle"
        />
        <span className="text-xs font-medium text-rose-800" title="Prémio manual one-shot. Entra como custo da empresa equivalente a 1 mês (valor anual = valor introduzido). Não aparece ao candidato.">
          Prémio Manual
        </span>
      </div>
      <span className="text-[10px] text-rose-600 font-medium">custo empresa</span>
    </div>
    {formData.include_manual_premium && (
      <div className="flex items-center gap-2">
        <Label className="text-[11px] text-rose-700 shrink-0">Valor (€)</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={formData.manual_premium_value ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            setFormData({
              ...formData,
              manual_premium_value: raw === "" ? undefined : (parseFloat(raw) || 0),
            });
          }}
          placeholder=""
          className="h-8 text-xs"
          data-testid="manual-premium-value-input"
        />
      </div>
    )}

    {/* ADMIN-ONLY: Excluir o Prémio automático (dos escalões de /admin/premiums)
        do custo da empresa para ESTA simulação. Útil quando o admin quer
        decidir manualmente (via Prémio Manual acima) ou simplesmente não
        atribuir prémio. Não afecta o Prémio Manual. */}
    <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
      <div className="flex items-center gap-2">
        <Switch
          checked={!!formData.exclude_auto_premium}
          onCheckedChange={(v) => setFormData({ ...formData, exclude_auto_premium: v })}
          className="scale-75"
          data-testid="exclude-auto-premium-toggle"
        />
        <span
          className="text-xs font-medium text-slate-700"
          title="Quando activo, o Prémio automático calculado pelos escalões de /admin/premiums NÃO entra como custo da empresa nesta simulação. Se quiser atribuir um prémio, use o Prémio Manual acima."
        >
          Excluir Prémio Auto
        </span>
      </div>
      <span className="text-[10px] text-slate-500 font-medium">só nesta simulação</span>
    </div>
  </div>
);

